import {createModel} from '@rematch/core';

import {WebsiteThemes} from '~/utils/constants';
import {Modals} from '~/modals/constants';
import {MODAL_REACT_SHOW, MANAGE_MENU_SHOW, REACT_REFRESH_ACCOUNT_DATA} from '~/loader/types';
import {fetchNavigationAds} from '~/services/hotsearch/promotions';
import {isAndroid, isMobileDevice} from '~/utils/browserDetect';

import {AppConfig, ManageMenuBootstrap} from '../../../types';
import type {RootModel} from '.';

export interface ManageMenu {
  visible: boolean;
  content: ManageMenuBootstrap;
}

export type AppState = {
  loading: boolean;
  manageMenu: ManageMenu;
  isMenuLocked: boolean;
  isSnackbarVisible: boolean;
  data: AppConfig;
  supportWidget: {
    loading: boolean;
    success: boolean;
  };
};

const initialConfig: AppConfig = {
  page: {
    isRootPage: false,
    isSearchPage: false, // any search page (grid & single place/model)
    isModelOrPlacePage: false,
    isModal: false,
    isLanding: false,
    pageCategory: '',
  },
  mail: {
    display: false,
    href: '',
  },
  impersonate: {
    display: false,
    email: '',
    userEmail: '',
  },
  balance: {
    display: false,
    amount: 0,
    formattedAmount: '',
  },
  logo: {
    display: false,
    title: '',
  },
  searchForm: {
    value: '',
    utmSource: null,
    analyzer: null,
    imageUploadLimits: {
      minHeight: 0,
      minWidth: 0,
      sizeMax: 0,
      sizeMin: 0,
    },
    searchAction: '',
    searchByPhoto: false,
    searchByPhotoBlob: '',
    uploadUrl: '',
  },
  instanceInfo: {
    label: 'HOT.com',
    countryCode: 'US',
    searchDomain: 'hot.com',
  },
  geo: {
    location: null,
    isClientLocationDetected: false,
  },
  theme: {
    code: WebsiteThemes.THEME_NATURE,
    name: '',
  },
  flags: {
    showNavTabs: false,
    showHeaderActions: false,
    showHeaderSearch: false,
    showSnackbar: false,
    addBusinessEnabled: false,
    isStandaloneApplication: false,
    showBottomMenu: false,
  },
  promos: {
    cams: {
      bid_price: '',
      url: '',
    },
    dating: {
      bid_price: '',
      url: '',
    },
    trackingToken: '',
  },
  account: {
    isGuest: true,
  },
  modals: {
    showRemovalRequest: false,
    showContactUs: false,
  },
  support: {
    enabled: false,
    lang: '',
    languages: [],
    widgetId: '',
    propertyId: '',
    user: null,
  },
};

const defaultState: AppState = {
  loading: true,
  data: initialConfig,
  manageMenu: {
    visible: false,
    content: null,
  },
  supportWidget: {
    loading: true,
    success: false,
  },
  isMenuLocked: false,
  isSnackbarVisible: false,
};

export const app = createModel<RootModel>()({
  state: defaultState,
  reducers: {
    setMenuLocked: (state, isLocked: boolean) => ({
      ...state,
      isMenuLocked: isLocked,
    }),
    setBootstrapData: (state, data: Partial<AppState>) => ({
      ...state,
      ...data,
    }),
    showManageMenu: (state, content: ManageMenuBootstrap) => ({
      ...state,
      manageMenu: {
        visible: true,
        content,
      },
    }),
    hideManageMenu: (state) => ({
      ...state,
      manageMenu: {
        visible: false,
        content: null,
      },
    }),
    setSupportWidgetLoaded: (state, success: boolean) => ({
      ...state,
      supportWidget: {
        loading: false,
        success,
      },
    }),
    setupNavigationAds: (state, response: any) => {
      const promos = {
        ...state.data.promos,
      };

      if (response.cams) {
        promos.cams = response.cams;
      }

      if (response.dating) {
        promos.dating = response.dating;
      }

      return {
        ...state,
        data: {
          ...state.data,
          promos,
        },
      };
    },
    toggleSnackBar: (state, isVisible: boolean) => ({
      ...state,
      isSnackbarVisible: isVisible,
    }),
  },
  effects: (dispatch) => {
    window.addEventListener('DOMContentLoaded', () => {
      dispatch.app.bootstrap();
    });

    window.document.addEventListener(MODAL_REACT_SHOW, ({detail}) => {
      if (Modals[detail.id]) {
        dispatch.modals.showModal({key: Modals[detail.id], payload: detail});
      } else {
        console.warn('Modal not found', Modals[detail.id]);
      }
    });

    window.document.addEventListener(MANAGE_MENU_SHOW, ({detail}) => {
      dispatch.app.showManageMenu(detail);
    });

    window.document.addEventListener(REACT_REFRESH_ACCOUNT_DATA, () => {
      dispatch.profile.getCurrentUserInfo();
    });

    return {
      async bootstrap() {
        dispatch.app.setBootstrapData({
          data: window.APP_CONFIG,
          loading: false,
          manageMenu: {
            content: window.APP_MANAGE_MENU,
            visible: Boolean(window.APP_MANAGE_MENU),
          },
          isSnackbarVisible:
            window.APP_CONFIG.flags.showSnackbar && isMobileDevice() && isAndroid(),
        });
      },
      async setupNavPromotions(_: void, state) {
        const {success, loading} = state.loading.effects.app.setupNavPromotionsRequest;

        if (success || loading) {
          return;
        }

        dispatch.app.setupNavPromotionsRequest();
      },
      async setupNavPromotionsRequest(_: void, state) {
        const {trackingToken} = state.app.data.promos;

        try {
          const response = await fetchNavigationAds(trackingToken);

          if (!response.data) {
            console.error('Error fetching ad buttons');
            return;
          }

          dispatch.app.setupNavigationAds(response.data);
        } catch (e) {
          console.error('Error fetching navigation ads', e);
        }
      },
      focusSearchInput() {
        const input = document.getElementById('search-input');

        if (!input) {
          console.error('Cant find main search input');
          return;
        }

        window.scrollTo({
          top: 0,
          behavior: 'smooth',
        });

        input.focus();
      },
    };
  },
});
